import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { DatePipe } from '@angular/common';
import { map, of, startWith } from 'rxjs';
import { RemoteWorkExceptionRequest, UpdateRemoteWorkExceptionRequestBody } from 'src/app/models/remote_work_exception_request.model';
import {
  EmployeeRequestResponsibleForAnswerStatusEnum,
  EmployeeRequestResponsibleForAnswerStatusLabels,
} from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { EmployeeRequestResponsibleForAnswersRequestableTypeEnum } from 'src/app/enums/employee-request-responsible-for-answers-requestable-type.enum';
import { RemoteWorkWeekdayExceptionReasonTypeEnumLabels } from 'src/app/enums/remote-work-weekday-exception-reason-type.enum';
import { ApiService, UserService } from 'src/app/services';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface RemoteWorkRequestReviewDialogData {
  remote_work_exception_request: RemoteWorkExceptionRequest;
  is_rrhh_view: boolean;
}

@Component({
  selector: 'app-remote-work-request-review-dialog',
  templateUrl: './remote-work-request-review-dialog.component.html',
  styleUrl: './remote-work-request-review-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatButton, DatePipe, MatProgressSpinnerModule],
})
export class RemoteWorkRequestReviewDialogComponent {
  readonly EmployeeRequestResponsibleForAnswerStatusEnum = EmployeeRequestResponsibleForAnswerStatusEnum;
  readonly EmployeeRequestResponsibleForAnswerStatusLabels = EmployeeRequestResponsibleForAnswerStatusLabels;
  readonly RemoteWorkWeekdayExceptionReasonTypeEnumLabels = RemoteWorkWeekdayExceptionReasonTypeEnumLabels;

  private readonly dialogRef = inject(MatDialogRef<RemoteWorkRequestReviewDialogComponent>);
  private readonly api = inject(ApiService);
  private readonly userService = inject(UserService);
  private readonly destroy_ref = inject(DestroyRef);
  readonly data = inject<RemoteWorkRequestReviewDialogData>(MAT_DIALOG_DATA);
  readonly request = this.data.remote_work_exception_request;
  readonly isRrhhView = this.data.is_rrhh_view;

  readonly loading = signal(false);

  private readonly currentUserId = this.userService.getCurrentUser()?.id;

  private readonly currentUserHasPendingAnswer = toSignal(
    this.currentUserId === undefined
      ? of(false)
      : this.api
          .getEmployeeRequestResponsibleForAnswers({
            requestable_type: EmployeeRequestResponsibleForAnswersRequestableTypeEnum.RemoteWorkWeekdayExceptionRequest,
            requestable_id: this.request.id,
            responsible_user_id: this.currentUserId,
            status: EmployeeRequestResponsibleForAnswerStatusEnum.PENDING,
          })
          .pipe(
            map(({ data }): boolean => data.length > 0),
            startWith(false)
          ),
    { initialValue: false }
  );

  readonly isPending = computed<boolean>(() => this.request.status === EmployeeRequestResponsibleForAnswerStatusEnum.PENDING);

  readonly canRespond = computed<boolean>(() => this.isPending() && this.currentUserHasPendingAnswer());

  submit(status: EmployeeRequestResponsibleForAnswerStatusEnum): void {
    if (this.loading() || !this.canRespond()) return;

    this.loading.set(true);

    const body: UpdateRemoteWorkExceptionRequestBody = { status };

    this.api
      .updateRemoteWorkExceptionRequest(this.request.id, body)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => this.dialogRef.close(true),
        error: (): void => {
          this.loading.set(false);
        },
      });
  }

  accept(): void {
    this.submit(EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED);
  }

  reject(): void {
    this.submit(EmployeeRequestResponsibleForAnswerStatusEnum.REJECTED);
  }

  close(): void {
    if (!this.loading()) this.dialogRef.close(null);
  }
}
